import { useParams } from 'react-router-dom';
import chatStore from 'shared/store/chatStore';
import constraintsStore from 'shared/store/constraintsStore';
import constraintProfilesStore from 'shared/store/constraintProfilesStore';

/**
 * The lifecycle state of the constraints panel for the CURRENT thread.
 *
 *   - `no_thread`  : `/assistant` route, no thread yet. Default-on-start
 *                    has been applied, user can edit/Invia to start a thread.
 *   - `connecting` : a thread URL is open but we don't yet have its metadata
 *                    in our local mirror (SDK is fetching, or we still hold
 *                    the previous thread's metadata from before the
 *                    navigation). User actions MUST be blocked here.
 *   - `syncing`    : a metadata write (silent sync via seedThread, Invia,
 *                    or first chat message) is awaiting its echo. Stacking
 *                    another write would race the outstanding one.
 *   - `hydrated`   : the panel's local state matches the thread's metadata
 *                    and the catalog is loaded. Safe to act.
 *   - `catalog_loading` : initial app startup before the profile catalog +
 *                    predefiniti caches are populated. Gated globally by
 *                    `AssistantPage`'s opaque overlay; this value is only
 *                    seen if a consumer reads the state before the overlay
 *                    is removed.
 */
export type ThreadConstraintsState =
  | 'catalog_loading'
  | 'no_thread'
  | 'connecting'
  | 'syncing'
  | 'hydrated';

/**
 * Derive the per-thread lifecycle state from existing signals. No new server
 * state — every input is already in one of the three stores.
 *
 *   - `useParams().threadId` is the URL truth.
 *   - `chatStore.metadataThreadId` is the URL id the current `threadMetadata`
 *     snapshot was delivered for. Equal to `urlThreadId` iff the SDK has
 *     finished hydrating the current navigation.
 *   - `constraintsStore.syncInFlight` is true while a write is awaiting
 *     its SDK promise to resolve (seedThread / continueThread /
 *     createThreadAndStart). We surface it here so the action buttons can
 *     be disabled instead of letting the user stack writes.
 *   - `constraintProfilesStore.loaded` is the global catalog gate. Until it
 *     flips true the per-thread state has no meaning (predefiniti / catalog
 *     are not yet there to diff against).
 */
export const useThreadConstraintsState = (): ThreadConstraintsState => {
  const urlThreadId = useParams().threadId;
  const metadataThreadId = chatStore((s) => s.metadataThreadId);
  const threadMetadata = chatStore((s) => s.threadMetadata);
  const syncInFlight = constraintsStore((s) => s.syncInFlight);
  const catalogLoaded = constraintProfilesStore((s) => s.loaded);

  if (!catalogLoaded) return 'catalog_loading';
  if (!urlThreadId) return 'no_thread';
  if (metadataThreadId !== urlThreadId || !threadMetadata) return 'connecting';
  if (syncInFlight) return 'syncing';
  return 'hydrated';
};

/**
 * True only when the panel is safe to accept user actions (Reset / Salva /
 * Invia / dropdown apply / profile delete / save dialog).
 *
 *   - `hydrated`: real settled state.
 *   - `no_thread`: fresh chat, default-on-start has set workingDocument, the
 *     user is allowed to edit constraints and Invia to create the thread.
 *
 * Anything else (`catalog_loading`, `connecting`, `syncing`) blocks actions.
 */
export const isThreadConstraintsActionable = (
  state: ThreadConstraintsState,
): boolean => state === 'hydrated' || state === 'no_thread';
